// src/host/ForeignTrust.js
//
// Contract section 12.4. Whether a foreign plugin may be loaded at all.
//
// A foreign plugin runs in the host's document with the host's privileges, and
// the only thing standing between a person and that is this decision. So it is
// a module of its own rather than a condition inside the loader: the rules are
// stated in one place, they are tested, and nothing can reach the loading path
// without passing through them.
//
// Three rules, and each exists because the obvious implementation gets it wrong:
//
// **Consent is bound to the container digest, not just to the plugin.** An IRI
// serves whatever is at it today. Consenting to a plugin rather than to a body
// of code would mean consenting once to everything the author ever publishes
// there, which is the substitution the whole of section 3.2 is about.
//
// **There is no blanket setting.** A checkbox that consents to all foreign
// plugins turns a decision about one body of code into a decision about a
// category, which is not a decision a person can make.
//
// **A stored project is not authority.** A project naming a foreign plugin is
// data from wherever it came from. Loading it on open would let anyone who can
// hand you a project file run code in your page.

/** A stable key for one body of code at one identity. */
const keyFor = (iri, digest) => `${iri} ${digest}`

export class ConsentRequired extends Error {
  constructor (request) {
    super(
      `${request.label ?? request.iri} is a foreign plugin and has not been consented to. ` +
      'It would run in this page with this page\'s privileges. Contract section 12.4.'
    )
    this.name = 'ConsentRequired'
    this.request = request
  }
}

/**
 * The record of what a person has agreed to run.
 *
 * `store` is injected so a host can persist it; the default forgets everything
 * when the page closes, which is the safer default and the one a test wants.
 * A store is a plain Map-like: get, set, delete, keys.
 */
export class ForeignTrust {
  #store

  constructor ({ store = new Map() } = {}) {
    this.#store = store
  }

  /**
   * What a host must put in front of a person before loading.
   *
   * Returned as data rather than rendered here, because this module must not
   * know what a dialog looks like, and because a test can assert on the words.
   * The three statements are what section 12.4 requires: which plugin, which
   * format, and what privilege it gets.
   */
  static request ({ iri, label, format, digest }) {
    return Object.freeze({
      iri,
      label: label ?? iri,
      format,
      digest,
      statements: Object.freeze([
        `${label ?? iri} is a ${format ?? 'foreign'} plugin.`,
        'It runs in this page, with this page\'s privileges. It can read this project, ' +
        'anything this page has stored, and every other plugin loaded here.',
        'It is not sandboxed and its capabilities cannot be limited.',
        `Only this exact copy is being agreed to (${(digest ?? '').slice(0, 19)}…). ` +
        'If it is updated, you will be asked again.'
      ])
    })
  }

  /** True only for this plugin and only for this container. */
  isConsented (iri, digest) {
    if (!iri || !digest) return false
    return this.#store.get(keyFor(iri, digest)) === true
  }

  /**
   * Record a person's decision.
   *
   * Takes the digest as an argument rather than reading it from anywhere later,
   * so that consenting to one body of code cannot be made to cover another.
   */
  consent (iri, digest) {
    if (!iri || !digest) throw new Error('consent needs both the plugin IRI and its container digest')
    this.#store.set(keyFor(iri, digest), true)
  }

  /** Withdraw it. The next load asks again. */
  revoke (iri, digest) {
    this.#store.delete(keyFor(iri, digest))
  }

  /**
   * Throws unless this exact container has been consented to.
   *
   * Throws rather than returning false so that a caller cannot proceed by
   * forgetting to check, which is the same reason verifyIntegrity throws.
   */
  require ({ iri, label, format, digest }) {
    if (!this.isConsented(iri, digest)) {
      throw new ConsentRequired(ForeignTrust.request({ iri, label, format, digest }))
    }
    return true
  }
}

/**
 * The foreign plugins a project refers to, for a host to ask about before it
 * opens one.
 *
 * Section 12.4: a project MUST NOT load a foreign plugin on open without
 * consent. A host calls this first and asks about what comes back, rather than
 * discovering it half way through building an audio graph.
 */
export function foreignPluginsIn (project, resolve) {
  const found = []
  for (const node of project?.nodes ?? []) {
    const profile = resolve?.(node.plugin)
    if (profile?.kind === 'foreign') {
      found.push({ nodeId: node.id, iri: node.plugin, label: profile.label, digest: profile.container?.integrity })
    }
  }
  return found
}
